import React from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';

import Button from '../Button';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import LoanCardTooltip from '../LoanCardTooltip';

import { formatCurrency } from '../../utils/formatCurrency';

import './style.scss';
import { ricWorkflowName } from '../../utils/appConstants';

const LoanCard = ({ loanDetails, onSelect, workflowType, isMobile }) => {
  const ricWorkFlow = workflowType === ricWorkflowName;
  const handleSelect = () => {
    onSelect(loanDetails);
  };

  const renderAprStr = (item) => (Number(item.originationPercentage) ? 'NO INTEREST' : '0% APR');

  return (
    <div className={`offer-card-card ${isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? 'border-light' : 'border-gray'}`}>
      {loanDetails.isAutoPayOffer && (
        <div className="card-header">
          Recommended Offer
        </div>
      )}
      <div className="card-content">
        <section className="top-section">
          <FormRow className="py-3">
            <FormColumn>
              <h2 className="payment-amount mb-0" style={{ color: isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? '#0084F6' : '#1C1C1C' }}>
                {`$${formatCurrency(isMobile ? loanDetails.paymentAmount : loanDetails.monthlyPayment, 2) || '-'}`}
              </h2>
              <h5 className="text-center payment-description">Est. Monthly Payment</h5>
            </FormColumn>
            <FormColumn>
              {
                (!!Number(loanDetails.zipTerm) || !loanDetails.isAutoPayOffer) ? (
                  <>
                    <h2 className={`mb-0 no-interest ${isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? '' : 'PriceCard-apr-right'}`} style={{ color: isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? '#0084F6' : '#1C1C1C' }}>
                      {isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? renderAprStr(loanDetails) : 'No Promotion'}
                    </h2>
                    <h5 className="no-interest" style={{ color: '#000a2f' }}>
                      {isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? `If repaid within ${loanDetails.zipTerm} months* and enrolled in AutoPay` : 'Without AutoPay Enrollment'}
                    </h5>
                  </>
                ) : null
              }
            </FormColumn>
          </FormRow>
        </section>
        <section className="bottom-section py-3">
          {workflowType !== 'dtm-luwa' ? (
            <FormRow className="mb-1">
              <FormColumn className="label" offset={8}>
                <h5>Amount of Sale</h5>
              </FormColumn>
              <FormColumn className="value" offset={4}>
                <span style={{ color: isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? '#0084F6' : '#000A2F' }}>{`$${formatCurrency(loanDetails.approvedAmount, 2)}`}</span>
              </FormColumn>
            </FormRow>
          ) : null}
          <FormRow className="mb-1">
            <FormColumn className="label" offset={8}>
              <h5>Amount Financed</h5>
            </FormColumn>
            <FormColumn className="value" offset={4}>
              <span style={{ color: workflowType === 'dtm-luwa' && isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? '#0084F6' : '#000A2F' }}>{`$${formatCurrency(loanDetails.amountTaken, 2)}`}</span>
            </FormColumn>
          </FormRow>
          <FormRow className="mb-1">
            <FormColumn className="label" offset={8}>
              {ricWorkFlow ? '' : 'Loan'}
              {' '}
              Term
            </FormColumn>
            <FormColumn className="value" offset={4}>
              {`${loanDetails.term} Months`}
            </FormColumn>
          </FormRow>
          <FormRow className="mb-1">
            <FormColumn className="label" offset={8}>Interest Rate</FormColumn>
            <FormColumn className="value" offset={4}>
              {`${formatCurrency(isMobile ? loanDetails.rate : loanDetails.interestRate, 2)}%`}
            </FormColumn>
          </FormRow>
          <FormRow className="mb-1">
            <FormColumn className={`label ${workflowType === 'text2apply' ? '' : 'bigger'}`} offset={8}>
              Est. APR
            </FormColumn>
            <FormColumn offset={4} className={`value ${workflowType === 'text2apply' ? '' : 'bigger'}`}>
              {`${formatCurrency(loanDetails.apr, 4)}%`}
            </FormColumn>
          </FormRow>
          {workflowType === 'text2apply' && !!Number(loanDetails.originationPercentage) ? (
            <FormRow className="no-gutters">
              <FormColumn offset={8} className="label origination-field">
                Origination Fee
                <LoanCardTooltip title="Origination Fee" className="origination-fee-tooltip">
                  The Origination Fee may be up to 8% of the Amount Financed, is non-refundable, and is considered earned upon the funding of your loan. You can calculate the dollar amount of the Origination Fee for each offer listed above by multiplying the Amount Financed figure for that offer by .08 if the Origination Fee is 8%, by .05 if it is 5%, etc. For example, if the Origination Fee is 8% and if the Amount Financed is $1,000 the corresponding Origination Fee would be $80 ($1000 x .08 = $80). The total Principal Amount of your loan is the sum of the Amount Financed and the Origination Fee (i.e. if the Origination Fee is 8% and if the Amount Financed is $1,000 the Principal Amount of the loan would be $1080). Please review your Loan Agreement for additional terms and conditions.
                </LoanCardTooltip>
                <br />
                <small>Included in the monthly payment</small>
              </FormColumn>
              <FormColumn offset={4} className="value">
                {`${formatCurrency(Number(loanDetails.originationPercentage) * 100, 2) || '-'}%`}
              </FormColumn>
            </FormRow>
          ) : null}
        </section>
        <section className="select-button py-3">
          <FormRow>
            <FormColumn>
              <Button
                className={`btn ${isNil(loanDetails.isAutoPayOffer) || loanDetails.isAutoPayOffer ? 'btn-primary' : 'btn-secondary'}`}
                onClick={handleSelect}
                title={isNil(loanDetails.isAutoPayOffer) ? 'Continue' : (
                  loanDetails.isAutoPayOffer ? 'Continue with Autopay' : (
                    <span style={{ textUnderlineOffset: 2 }}>
                      Continue
                      {' '}
                      <u>without</u>
                      {' '}
                      Autopay
                    </span>
                  )
                )}
              />
            </FormColumn>
          </FormRow>
        </section>
        <section>
          {loanDetails.downPaymentRequired ? (
            <FormRow className={`downpayment-section no-gutters ${!isNil(loanDetails.isAutoPayOffer) && !loanDetails.isAutoPayOffer ? 'downpayment-section-grey' : ''}`}>
              <FormColumn>
                <p>
                  Down Payment
                  <sup>1</sup>
                </p>
              </FormColumn>
              <FormColumn>
                <p className="text-right">{`$${formatCurrency(loanDetails.downPaymentRequired || 0, 2)}`}</p>
              </FormColumn>
            </FormRow>
          ) : null}
        </section>
      </div>
    </div>
  );
};

LoanCard.propTypes = {
  loanDetails: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  workflowType: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
};

LoanCard.defaultProps = {
  isMobile: false,
};

export default LoanCard;
